import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ButtonNames } from '../../../../../contexts/main/biInterfaces';
import { Spinner } from '../../../../../components-shared/Spinner';
import {
  useBi,
  usePanorama,
  withBi,
  withExperiments,
} from '@wix/yoshi-flow-editor';
import { ToastType } from '../../../../../contexts/ToastContext/interfaces';
import { classes, st } from './SidebarLayoutForParticipant.st.css';
import '../../../../../components-shared/Ricos/rce/rce.global.scss';
import utils, {
  ChallengeEntityType,
  getFirstAvailableStep,
  getFlatStepsList,
  getProgramProgress,
  IFollowingEntity,
  isStepAvailableForComplete,
  isStepOverdue,
  isStepResolved,
  isVisibleStepLockedForCompleteOptimized,
} from '../utils';
import {
  getWixAdvertisementBannerHeight,
  setElementToViewport,
} from '../../../../../services/scrollHelpers';
import { getFirstAvailableStepFromSection } from '../../../../../selectors/sections';
import { memberWebAppButtonClick } from '@wix/bi-logger-challenges-member-web/v2';
import { withGeneralData } from '../../../../../contexts/GeneralDataProvider/withGeneralData';
import { withToast } from '../../../../../contexts/ToastContext/withToast';
import { ISidebarMobileScreenFromSettings } from '../../../Settings.mobile/tabs/Design/DesignSettings';
import { IFormHandleExtended } from '../../components/FormQuiz';
import { sidebarLayoutDataHooks } from './sidebarLayoutDataHooks';
import { withLeaveChallengeModal } from '../../../../../contexts/LeaveChallengeModal/LeaveChallengeModalContext';
import { LeaveModalType } from '../../../../../contexts/LeaveChallengeModal/LeaveChallengeModalProvider';
import { Challenges } from '../../../../../editor/types/Experiments';

import { getQuizId, getQuizSubmissionId } from '../../../../../selectors/step';
import { isVideoStep } from '../../../../../selectors/video';
import { ParticipantStep } from '../../../../../types/v3Types';
import { FocusPoint } from '../../../../../contexts/FocusManager';
import { FormError } from '@wix/form-viewer/widget';
import { SidebarWithChallenge } from './components/SidebarWithChallenge';
import { Props } from './interfaces';
import { getProgramSlug } from '../../../../../selectors/getProgramSlug';
import { Pages } from '../../../../../contexts/Location/LocationContext';
import { LayoutSelectedTab } from '../../../../ChallengesPage/DeprecatedSettings/tabs/Design/components/OverviewScheduleSwitcher/constants';
import { useAIChatData } from '../../../../../contexts/storage-contexts/AIChat';
import { ResolveStepParams } from '../../../../../contexts/ResolveStep/ResolveStepContext';
import { UpdateParticipantStepStatusPayload } from '../../../../../contexts/ParticipantStepsDataProvider/ParticipantStepsContext';

export function getResolveQuizPayload(
  currentStep: ParticipantStep | null,
  quizSubmissionId?: string,
): ResolveStepParams {
  return {
    stepId: currentStep?.id,
    isUpdate: isStepResolved(currentStep),
    unresolvedMessage: {
      type: ToastType.error,
      key: 'toast.quiz-completed-error',
    },
    quizSubmission: {
      quizSubmissionId,
      score: 100, // for the future
    },
  };
}

type ResolveCurrentStepForParticipantParams = {
  challenge: Props['challengeData']['challenge'];
  currentStep: ParticipantStep | null;
  flatStepsList: ParticipantStep[];
  listParticipantSections: Props['listParticipantSections'];
  options?: { background?: boolean };
  resolveStepProp: Props['resolveStep'];
  resolvingStepIds: Set<string>;
  steps: ParticipantStep[];
  updateParticipantStepStatus(
    payload: UpdateParticipantStepStatusPayload,
  ): void;
};

export async function resolveCurrentStepForParticipant({
  challenge,
  currentStep,
  flatStepsList,
  listParticipantSections,
  options,
  resolveStepProp,
  resolvingStepIds,
  steps,
  updateParticipantStepStatus,
}: ResolveCurrentStepForParticipantParams): Promise<void> {
  const stepForView = currentStep;

  if (!stepForView?.id) {
    console.error("Can't find step for resolve.");
    return;
  }

  const firstAvailableStep = getFirstAvailableStep(flatStepsList);
  const canCompleteStep =
    isStepAvailableForComplete(stepForView) || isStepOverdue(stepForView);
  const isLockedForComplete = isVisibleStepLockedForCompleteOptimized(
    challenge,
    stepForView,
    firstAvailableStep,
  );

  if (!canCompleteStep || isLockedForComplete) {
    return;
  }

  if (resolvingStepIds.has(stepForView.id)) {
    return;
  }

  resolvingStepIds.add(stepForView.id);

  try {
    const hasParticipantSections = !!listParticipantSections?.length;
    const { isStepResolved: isCurrentStepResolved, currentStepAfterUpdate } =
      utils.getResolveStepData(stepForView, null);

    updateParticipantStepStatus({
      stepId: currentStepAfterUpdate?.id,
      transitions: currentStepAfterUpdate?.transitions,
      steps,
      sections: hasParticipantSections ? listParticipantSections : [],
    });

    const resolvePayload = {
      stepId: stepForView.id,
      isUpdate: isCurrentStepResolved,
      background: options?.background,
    };

    await resolveStepProp(resolvePayload);
  } finally {
    resolvingStepIds.delete(stepForView.id);
  }
}

const SidebarComponent: React.FC<Props> = (props) => {
  const bi = useBi();
  const panorama = usePanorama();
  const { currentChatPair, getPersonaPair } = useAIChatData();
  const {
    isMobile,
    selectedStepId,
    experiments,
    isCssPerBreakpoint,
    resolveStep: resolveStepProp,
    settings,
    isEditor,
    participantSteps = { steps: [] },
    sidebarLayoutMobileScreen,
    isResolveStepRequestInProgress,
    setNextFocusPoint,
    showModal,
    participant,
    listParticipantSections,
    challengeData,
    isLoading,
    isFullWidthLayout,
    isRTL,
    isFullscreen,
    resolveStepData,
    resolveStepError,
    t,
    showToast,
    updateParticipantStepStatus,
  } = props;
  const flatStepsList = useMemo(
    () => getFlatStepsList({ listParticipantSections, participantSteps }),
    [listParticipantSections, participantSteps],
  );
  // Compute current step from selectedStepId and current participantSteps data
  // This ensures we always get the latest step data, even after updateParticipantStepStatus
  const currentStep = useMemo(() => {
    // If editor show selected tab;
    if (isEditor) {
      if (props.shownTab === LayoutSelectedTab.Overview) {
        return null;
      }
      return flatStepsList[0];
    }

    // If we have a selectedStepId from URL, find it in current steps
    if (selectedStepId) {
      const foundStep = flatStepsList.find(
        (step) => step?.id === selectedStepId,
      );
      if (foundStep) {
        return foundStep;
      }
    }

    // If no selectedStepId and overview is not displayed, get first available step
    if (!settings.overviewTabDisplay) {
      return getFirstAvailableStep(flatStepsList);
    }

    // Otherwise, no step selected (overview mode)
    return null;
  }, [
    flatStepsList,
    isEditor,
    props.shownTab,
    selectedStepId,
    settings.overviewTabDisplay,
  ]);

  // State
  const [wixAdvertisementBannerHeight, setWixAdvertisementBannerHeight] =
    useState<number | undefined>(undefined);
  const [mobileScreen, setMobileScreen] = useState<'list' | 'view'>(() => {
    // Auto-open view mode on mobile when there's a selected step on initial load
    if (isMobile && !isEditor && selectedStepId) {
      return 'view';
    }
    return 'list';
  });
  const [videoStepAccepted, setVideoStepAccepted] = useState<boolean>(false);
  const [isVideoLoadingError, setIsVideoLoadingError] =
    useState<boolean>(false);
  const [isAutoNavigateToNextStep, setIsAutoNavigateToNextStep] =
    useState<boolean>(false);
  const [isAutoPlayVideo, setIsAutoPlayVideo] = useState<boolean>(false);
  const [formValidity, setFormValidity] = useState<FormError[]>([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [isAiChatVisible, setIsAiChatVisible] = useState<boolean>(false);

  // Refs
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stepViewRef = useRef<HTMLDivElement>(null);
  const formQuizRef = useRef<IFormHandleExtended>(null);
  const resolvingStepIdsRef = useRef(new Set<string>());

  // Refs to track previous props for componentDidUpdate logic
  const prevIsResolveStepRequestInProgressRef = useRef(
    isResolveStepRequestInProgress,
  );
  // Check if collapsible sidebar experiment is enabled
  const isCollapsibleSidebarEnabled = experiments.enabled(
    Challenges.enableCollapsibleSidebar,
  );
  const isAiChatEnabled =
    experiments.enabled(Challenges.enableAiChat) &&
    currentChatPair !== null &&
    !currentChatPair?.disabled &&
    (currentChatPair?.syncStatus === 'synced' || currentChatPair.synced);
  // Handle sidebar collapse toggle
  const toggleSidebarCollapse = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  // Handle AI chat visibility toggle
  const toggleAiChatVisibility = useCallback(() => {
    setIsAiChatVisible((prev) => {
      const newAiChatVisible = !prev;
      void bi.report(
        memberWebAppButtonClick({
          buttonName: newAiChatVisible
            ? ButtonNames.OpenAiTutor
            : ButtonNames.CloseAiTutor,
        }),
      );
      return newAiChatVisible;
    });
  }, [bi]);

  // Fetch AI chat pair data when experiment is enabled
  useEffect(() => {
    const isAiChatExperimentEnabled = experiments.enabled(
      Challenges.enableAiChat,
    );
    if (isAiChatExperimentEnabled && challengeData?.challenge?.id) {
      // fetch the pair data to check if chat is properly configured
      // this must happen before we check isAiChatEnabled for UI rendering
      void getPersonaPair(challengeData.challenge.id);
    }
  }, [experiments, challengeData?.challenge?.id, getPersonaPair]);

  // Handle editor state (replaces getDerivedStateFromProps)
  useEffect(() => {
    if (isEditor) {
      if (sidebarLayoutMobileScreen === ISidebarMobileScreenFromSettings.List) {
        setMobileScreen('list');
      } else if (
        sidebarLayoutMobileScreen ===
          ISidebarMobileScreenFromSettings.Overview ||
        sidebarLayoutMobileScreen === ISidebarMobileScreenFromSettings.Schedule
      ) {
        setMobileScreen('view');
      }
    }
  }, [isEditor, sidebarLayoutMobileScreen]);

  // Helper functions
  const sendBiButtonClick = useCallback(
    (buttonName: ButtonNames, step?: string) => {
      void bi.report(
        memberWebAppButtonClick({
          buttonName,
          ...(step && { step }),
        }),
      );
    },
    [bi],
  );

  const currentStepId = currentStep?.id;

  // in mobile view, focus and scrollIntoViewport selected step in list
  useEffect(() => {
    if (currentStepId && isMobile && mobileScreen === 'list') {
      const btnDOM = document.querySelector(
        `li[data-id="${currentStepId}"] button`,
      );
      if (btnDOM) {
        (btnDOM as HTMLButtonElement).focus();
        btnDOM.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [currentStepId, isMobile, mobileScreen]);

  const preventScrollOnFirstRender = useRef(true);
  useEffect(() => {
    if (!preventScrollOnFirstRender.current) {
      setElementToViewport(stepViewRef.current);
    }
    preventScrollOnFirstRender.current = false;
  }, [currentStepId]);

  // Consolidated step navigation function
  const navigateToStep = useCallback(
    (
      targetStep: ParticipantStep | null,
      options: {
        withBI?: boolean;
        buttonName?: ButtonNames;
        skipFormCheck?: boolean;
      } = {},
    ) => {
      const { withBI = false, buttonName, skipFormCheck = false } = options;

      panorama.logger().info('Initiating navigation to step');

      if (isResolveStepRequestInProgress) {
        panorama
          .logger()
          .warn(
            'Navigation to step blocked since resolve step request is in progress',
          );

        return;
      }

      // Send BI tracking if requested
      if (withBI && buttonName) {
        sendBiButtonClick(buttonName, targetStep?.id);
      }

      const performNavigation = () => {
        panorama.logger().info('Performing navigation to step');

        setNextFocusPoint?.(FocusPoint.Top);

        const challengeSlug = getProgramSlug(challengeData?.challenge);

        // Always set mobile screen to 'view' when navigating to a step on mobile
        if (isMobile) {
          setMobileScreen('view');
        }

        if (targetStep) {
          // Navigate to step
          if (challengeSlug && props.selectStep) {
            void props.selectStep(challengeSlug, targetStep?.id);
          }
        } else {
          // Navigate to overview/description
          if (challengeSlug) {
            void props.selectStep(challengeSlug, null);
            if (props.goToPage) {
              void props.goToPage({
                pageId: Pages.Participant,
                challengeId: challengeSlug,
                deepLink: 'description',
              });
            }
          }
        }
      };

      // Check if form is dirty and handle accordingly
      if (!skipFormCheck && formQuizRef?.current?.dirty) {
        panorama.logger().warn('Form is dirty, showing leave session modal');

        showModal(
          {
            participantId: participant?.id,
            modalType: LeaveModalType.LeaveSession,
          },
          undefined,
          performNavigation,
        );
      } else {
        performNavigation();
      }
    },
    [
      isResolveStepRequestInProgress,
      sendBiButtonClick,
      setNextFocusPoint,
      challengeData?.challenge,
      isMobile,
      props,
      formQuizRef,
      showModal,
      participant?.id,
      panorama,
    ],
  );

  // Simplified setStep function - now uses navigateToStep
  const setStep = useCallback(
    (currentStepParam: ParticipantStep) => {
      navigateToStep(currentStepParam, {
        skipFormCheck: true, // setStep doesn't check form dirty state
      });
    },
    [navigateToStep],
  );

  // Simplified onStepChosen function - now uses navigateToStep
  const onStepChosen = useCallback(
    (step: ParticipantStep): void => {
      const buttonName = step
        ? ButtonNames.StepTileSelection
        : ButtonNames.OverviewTileSelection;

      navigateToStep(step, {
        withBI: true,
        buttonName,
      });
    },
    [navigateToStep],
  );

  const onNextEntity = useCallback(
    async (nextEntity: IFollowingEntity, withBI = true): Promise<void> => {
      switch (nextEntity.type) {
        case ChallengeEntityType.STEP:
          // Use navigateToStep directly for next navigation to avoid duplicate BI
          navigateToStep(nextEntity.step, {
            withBI,
            buttonName: withBI ? ButtonNames.NextStep : undefined,
          });
          break;
        default:
          return null;
      }
    },
    [navigateToStep],
  );

  const checkAutoNavigate = useCallback(async () => {
    if (isAutoNavigateToNextStep) {
      const nextEntity = utils.getFollowingEntity(currentStepId, {
        listParticipantSections,
        participantSteps,
      });
      const isNextVideo = isVideoStep((nextEntity as any)?.step?.source);

      setIsAutoNavigateToNextStep(false);
      setIsAutoPlayVideo(isNextVideo ? isAutoPlayVideo : false);

      await onNextEntity(nextEntity, false);
    }
  }, [
    isAutoNavigateToNextStep,
    currentStepId,
    listParticipantSections,
    participantSteps,
    isAutoPlayVideo,
    onNextEntity,
  ]);

  const { challenge } = challengeData;
  const { steps = [] } = participantSteps;

  const resolveStep = useCallback(
    async (options?: { background?: boolean }): Promise<void> => {
      await resolveCurrentStepForParticipant({
        challenge,
        currentStep,
        flatStepsList,
        listParticipantSections,
        options,
        resolveStepProp,
        resolvingStepIds: resolvingStepIdsRef.current,
        steps,
        updateParticipantStepStatus,
      });
    },
    [
      challenge,
      currentStep,
      flatStepsList,
      listParticipantSections,
      resolveStepProp,
      steps,
      updateParticipantStepStatus,
    ],
  );

  const onStepResolve = useCallback(
    async (_step?: ParticipantStep, options?: { background?: boolean }) => {
      sendBiButtonClick(ButtonNames.StepComplete);
      void resolveStep(options);
    },
    [resolveStep, sendBiButtonClick],
  );

  const resolveQuiz = useCallback(
    async (quizSubmissionId?: string) => {
      const stepForView = currentStep;

      if (stepForView?.id) {
        await resolveStepProp(
          getResolveQuizPayload(stepForView, quizSubmissionId),
        );
      } else {
        console.error("Can't find step for resolve.");
      }
    },
    [currentStep, resolveStepProp],
  );

  const getIsStartDisabled = useCallback(() => {
    const isSections = listParticipantSections?.length;
    if (isSections) {
      const nextStep = getFirstAvailableStepFromSection(
        listParticipantSections || [],
      );
      return !nextStep;
    }

    return false;
  }, [listParticipantSections]);

  const goToMobileList = useCallback(() => {
    if (isMobile) {
      setMobileScreen('list');
    }
  }, [isMobile]);

  const getQuizData = useCallback(() => {
    const step = currentStep;
    let isQuiz: boolean = false;
    let formId: string;
    let quizSubmissionId: string;
    if (step) {
      formId = getQuizId(step?.source);
      isQuiz = !!formId;
      quizSubmissionId = getQuizSubmissionId(step);
    }
    const isResolved = isStepResolved(currentStep);

    return {
      isQuiz,
      formId,
      quizSubmissionId,
      isResolved,
    };
  }, [currentStep]);

  const isCssPBEnabled = useCallback(
    (
      options: {
        includeParticipantPageAlignmentCheck: boolean;
      } = {
        includeParticipantPageAlignmentCheck: false,
      },
    ) => {
      const isCSSPBEnabledBasic =
        isCssPerBreakpoint && experiments.enabled('specs.programs.OOIStyleBP');
      const isCSSAlignmentEnabled = options.includeParticipantPageAlignmentCheck
        ? experiments.enabled('specs.programs.OOIParticipantPageCSSAlignment')
        : true;

      return isCSSPBEnabledBasic && isCSSAlignmentEnabled;
    },
    [experiments, isCssPerBreakpoint],
  );

  const isProgramFinished = useMemo(() => {
    return getProgramProgress(flatStepsList).isProgramFinished;
  }, [flatStepsList]);

  const goToCurrentStep = useCallback(() => {
    setStep(utils.getFirstAvailableStep(flatStepsList));
  }, [flatStepsList, setStep]);

  // ComponentDidMount equivalent
  useEffect(() => {
    setWixAdvertisementBannerHeight(getWixAdvertisementBannerHeight());
  }, []);

  // ComponentDidUpdate equivalent - handle resolve step request changes
  useEffect(() => {
    const isSections = listParticipantSections?.length;

    // Check if resolveStepRequestInProgress changed from true to false (request completed)
    const wasRequestInProgress = prevIsResolveStepRequestInProgressRef.current;

    const requestJustCompleted =
      wasRequestInProgress && !isResolveStepRequestInProgress;

    if (requestJustCompleted) {
      const step = resolveStepData;
      if (!resolveStepError) {
        updateParticipantStepStatus({
          replace: true,
          stepId: step?.id,
          transitions: step?.transitions,
          quizSubmission: step?.quizSubmission,
          steps,
          sections: isSections ? listParticipantSections : [],
        });

        void checkAutoNavigate();
      } else {
        showToast(t('toast.step-completed-error'), {
          type: ToastType.error,
        });
      }
    }

    // Update refs with current values for next render
    prevIsResolveStepRequestInProgressRef.current =
      isResolveStepRequestInProgress;
  }, [
    isResolveStepRequestInProgress,
    resolveStepData,
    resolveStepError,
    checkAutoNavigate,
    t,
    showToast,
    listParticipantSections,
    participantSteps,
    updateParticipantStepStatus,
    steps,
  ]);

  const cssPBEnabled = isCssPBEnabled();
  const withCSSAlignment = isCssPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  // Early return for invalid data
  if (!challengeData?.challenge) {
    return null;
  }

  // Show loading spinner
  if (isLoading) {
    return (
      <div
        className={classes.fullPageSpinner}
        data-hook={sidebarLayoutDataHooks.spinner()}
      >
        <Spinner />
      </div>
    );
  }

  // Main content
  return (
    <section
      data-hook={sidebarLayoutDataHooks.main()}
      className={st(classes.root, {
        mobile: isMobile,
        mobileScreen,
        newScrolling: true,
        fullWidthLayout: isFullWidthLayout,
        withoutSpace:
          !cssPBEnabled && parseInt(settings.sidebarLayoutSpace, 10) === 0,
        withSettingsSpace:
          !cssPBEnabled && parseInt(settings.sidebarLayoutSpace, 10) > 0,
        align: !withCSSAlignment ? settings.sidebarLayoutTextAlignment : null,
        withCSSAlignment,
        rtl: isRTL,
        fullscreen: isFullscreen,
        sidebarCollapsed: isCollapsibleSidebarEnabled && isSidebarCollapsed,
        aiChatVisible: !isMobile && isAiChatVisible,
      })}
    >
      <SidebarWithChallenge
        props={props}
        currentStep={currentStep}
        mobileScreen={mobileScreen}
        formQuizRef={formQuizRef}
        formValidity={formValidity}
        setFormValidity={setFormValidity}
        wixAdvertisementBannerHeight={wixAdvertisementBannerHeight}
        cssPBEnabled={cssPBEnabled}
        wrapperRef={wrapperRef}
        stepViewRef={stepViewRef}
        videoStepAccepted={videoStepAccepted}
        setVideoStepAccepted={setVideoStepAccepted}
        isVideoLoadingError={isVideoLoadingError}
        setIsVideoLoadingError={setIsVideoLoadingError}
        isAutoPlayVideo={isAutoPlayVideo}
        changeAutoplayOptions={(isAutoplayEnabled: boolean) => {
          return new Promise<void>((resolve) => {
            setIsAutoNavigateToNextStep(isAutoplayEnabled);
            setIsAutoPlayVideo(isAutoplayEnabled);
            resolve();
          });
        }}
        checkAutoNavigate={checkAutoNavigate}
        goToCurrentStep={goToCurrentStep}
        resolveCurrentStep={resolveStep}
        onStepChosen={onStepChosen}
        goToMobileList={goToMobileList}
        getQuizData={getQuizData}
        resolveQuiz={resolveQuiz}
        onStepResolve={onStepResolve}
        onNextEntity={onNextEntity}
        getIsStartDisabled={getIsStartDisabled}
        setStep={setStep}
        sendBiButtonClick={sendBiButtonClick}
        isProgramFinished={isProgramFinished}
        isCollapsibleSidebarEnabled={isCollapsibleSidebarEnabled}
        isSidebarCollapsed={isSidebarCollapsed}
        toggleSidebarCollapse={toggleSidebarCollapse}
        isAiChatVisible={isAiChatVisible}
        isAiChatEnabled={isAiChatEnabled}
        toggleAiChatVisibility={toggleAiChatVisibility}
      />
    </section>
  );
};

SidebarComponent.displayName = 'ChallengePage';

export default withExperiments<any>(
  withBi(withGeneralData(withToast(withLeaveChallengeModal(SidebarComponent)))),
);
